<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import * as echarts from 'echarts'
import type { ECharts, EChartsOption } from 'echarts'
import type { CSSProperties } from 'vue'

export interface WidgetChartBaseProps {
  chartOption: EChartsOption
  theme?: 'dark' | 'light' | undefined
  minHeight?: string
  maxHeight?: string
  afterChartInstanceCreated?: (chartInstance: ECharts) => void
}

const props = withDefaults(defineProps<WidgetChartBaseProps>(), {
  theme: undefined,
  minHeight: '24rem',
  maxHeight: '24rem',
})

// DOM reference for the chart container
const chartDomRef = ref<HTMLDivElement | null>(null)
// Reference to store the ECharts instance
const chartInstanceRef = ref<ECharts | null>(null)
// Reference for the ResizeObserver instance
const observerRef = ref<ResizeObserver | null>(null)

// --- CONSTANTS AND SCALING LOGIC ---
const BASE_CHART_WIDTH = 500 // Reference width for scaling
const BASE_FONT_SIZES = {
  default: 12,
  large: 16,
  small: 10,
}

const updateFontSizes = (chartInstance: ECharts, option: EChartsOption) => {
  const currentWidth = chartInstance.getWidth()
  if (currentWidth === 0) return

  const scaleFactor = currentWidth / BASE_CHART_WIDTH

  const scaleText = (baseSize: number | undefined): number | undefined => {
    const size = baseSize ?? BASE_FONT_SIZES.default
    const scaledSize = size * scaleFactor
    return Math.max(8, scaledSize)
  }

  // Deep clone the option to safely modify it
  const updatedOption = JSON.parse(JSON.stringify(option)) as EChartsOption

  // --- A. Components (Title, Legend) ---
  const components = [updatedOption.title, updatedOption.legend]
  components
    .flat()
    .filter(Boolean)
    .forEach((comp: any) => {
      if (comp?.textStyle) {
        comp.textStyle.fontSize = scaleText(comp.textStyle.fontSize)
      }
      if (comp?.itemStyle?.textStyle) {
        comp.itemStyle.textStyle.fontSize = scaleText(comp.itemStyle.textStyle.fontSize)
      }
    })

  // --- B. Axes (xAxis, yAxis) ---
  const axes = [updatedOption.xAxis, updatedOption.yAxis]
  axes
    .flat()
    .filter(Boolean)
    .forEach((axis: any) => {
      if (axis?.axisLabel?.fontSize) {
        axis.axisLabel.fontSize = scaleText(axis.axisLabel.fontSize)
      }
    })

  // --- C. Series ---
  const seriesArray = Array.isArray(updatedOption.series)
    ? updatedOption.series
    : updatedOption.series
      ? [updatedOption.series]
      : []

  seriesArray
    .flat()
    .filter(Boolean)
    .forEach((s: any) => {
      if (s?.label?.fontSize) {
        s.label.fontSize = scaleText(s.label.fontSize)
      }
      if (s.type === 'gauge') {
        if (s.title?.fontSize) {
          s.title.fontSize = scaleText(s.title.fontSize)
        }
        if (s.detail?.fontSize) {
          s.detail.fontSize = scaleText(s.detail.fontSize)
        }
        if (s.axisLabel?.fontSize) {
          s.axisLabel.fontSize = scaleText(s.axisLabel.fontSize)
        }
      }
      if (s.markPoint?.label?.fontSize) {
        s.markPoint.label.fontSize = scaleText(s.markPoint.label.fontSize)
      }
      if (s.markLine?.label?.fontSize) {
        s.markLine.label.fontSize = scaleText(s.markLine.label.fontSize)
      }
    })

  chartInstance.setOption(updatedOption, false)
}

// --- LIFECYCLE HOOKS  ---
// The main resize handler function
const resizeHandler = () => {
  const chart = chartInstanceRef.value
  if (!chart) {
    return
  }

  chart.resize()
  // Use the prop option as the base for scaling
  updateFontSizes(chart as any, props.chartOption)
}

// Initialization
onMounted(() => {
  if (!chartDomRef.value) {
    return
  }

  const chartDom = chartDomRef.value

  // 1. Initialize ECharts instance
  const chartInstance = echarts.init(chartDom, props.theme)
  chartInstanceRef.value = chartInstance

  // 2. Setup ResizeObserver
  observerRef.value = new ResizeObserver(resizeHandler)
  observerRef.value.observe(chartDom)

  // 3. Initial setting and font update
  chartInstance.setOption(props.chartOption)
  updateFontSizes(chartInstance, props.chartOption)

  resizeHandler() // Initial resize to ensure correct sizing/fonts immediately

  // 4. Call external callback
  props.afterChartInstanceCreated?.(chartInstance)
})

// Cleanup
onUnmounted(() => {
  const chartDom = chartDomRef.value
  const observer = observerRef.value
  const chart = chartInstanceRef.value

  // 1. Safely disconnect the observer
  if (chartDom && observer) {
    observer.unobserve(chartDom)
  }
  observer?.disconnect()

  // 2. Dispose of the ECharts instance
  chart?.dispose()
  chartInstanceRef.value = null
})

// Prop Update Logic
watch(
  () => props.chartOption,
  (newOption) => {
    const chart = chartInstanceRef.value
    if (chart) {
      // Update chart option when prop changes
      chart.setOption(newOption)
      // Re-apply font sizing
      updateFontSizes(chart as any, newOption)
    }
  },
  { deep: true },
) // Deep watch is often necessary for ECharts options

// Theme Change
// In Vue, watching a prop that requires full re-initialization is often done by
// manually tearing down and recreating the component, or by handling the lifecycle logic here.
// For simplicity, we'll keep the theme static after mount.
// If the theme needed to change, the component would typically be remounted via a parent key change.

// STYLES
const styleChartDom = computed<CSSProperties>(() => {
  const minHeight = props.minHeight ?? '24rem'
  const maxHeight = props.maxHeight ?? '24rem'
  const height = 'calc(100vh - 480px)' // Hardcoded height calculation

  // Vue's binding (:style) handles dynamic object merging
  return {
    position: 'relative',
    height: height,
    minHeight: minHeight,
    maxHeight: maxHeight,
  }
})
</script>

<template>
  <div class="w-full h-min">
    <div class="chart-container block h-full">
      <div ref="chartDomRef" :style="styleChartDom"></div>
    </div>
  </div>
</template>
